import { useState } from "react";
import { Calculator, ArrowUpRight, Wind } from "lucide-react";
import { SPECS } from "./data";
import { Reveal, scrollToId } from "./motion";

const flowOf = (s) => parseFloat(s.flow.replace(/,/g, ""));

const MAX_UNITS = 10;

const recommend = (required, northFacing) => {
  const pool = SPECS.filter(
    (s) => s.placement !== "Gable/Wall" && s.model !== "SAV-60T" && s.model !== "SAV-120T"
  ).filter((s) => northFacing || s.placement === "All directions");
  const single = pool.filter((s) => flowOf(s) >= required).sort((a, b) => flowOf(a) - flowOf(b))[0];
  if (single) return { units: 1, model: single };
  const sav20 = SPECS.find((s) => s.model === (northFacing ? "SAV-20" : "SAV-20T"));
  const units = Math.ceil(required / flowOf(sav20));
  if (units > MAX_UNITS) return { units, model: sav20, oversized: true };
  return { units, model: sav20 };
};

export const AirflowCalculator = () => {
  const [area, setArea] = useState("");
  const [height, setHeight] = useState("1.5");
  const [north, setNorth] = useState(true);

  const a = parseFloat(area);
  const h = parseFloat(height);
  const valid = a > 0 && h > 0;
  const volume = valid ? a * h : 0;
  const required = valid ? Math.round(volume * 10) : 0;
  const rec = valid ? recommend(required, north) : null;

  const inputCls =
    "w-full bg-[#050505] border border-[#333] rounded-xl px-5 py-4 text-white font-mono-eng text-lg focus:border-[#fcc41b] focus:outline-none transition-colors duration-300";

  return (
    <section id="calculator" data-testid="airflow-calculator" className="relative py-28 lg:py-40 bg-[#0a0a0a] border-t border-[#1c1c1c]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <Reveal><p className="text-xs tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-5">Airflow Calculator</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display font-semibold tracking-tighter leading-[0.95] text-white mb-8" style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}>
                Which model does<br />your roof need?
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-zinc-400 leading-relaxed max-w-md mb-8">
                Based on the 10x air changes per hour benchmark: we multiply your roof cavity volume by ten to find the airflow your ventilator needs to move every hour.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="space-y-6 max-w-md">
                <label className="block">
                  <span className="block font-mono-eng text-[11px] tracking-wider uppercase text-zinc-500 mb-2">Roof area (m²)</span>
                  <input
                    type="number"
                    min="1"
                    inputMode="decimal"
                    placeholder="e.g. 200"
                    value={area}
                    onChange={(e) => setArea(e.target.value)}
                    data-testid="calc-area-input"
                    className={inputCls}
                  />
                </label>
                <label className="block">
                  <span className="block font-mono-eng text-[11px] tracking-wider uppercase text-zinc-500 mb-2">Roof cavity height (m) — enter ½ of your peak cavity height</span>
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    inputMode="decimal"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    data-testid="calc-height-input"
                    className={inputCls}
                  />
                  <span className="block mt-2 text-zinc-500 text-xs leading-relaxed">
                    E.g. if your roof peaks at 3 m above the ceiling, enter 1.5. Halving the peak height accounts for the sloping roof shape.
                  </span>
                </label>
                <div>
                  <span className="block font-mono-eng text-[11px] tracking-wider uppercase text-zinc-500 mb-2">Do you have a north-facing roof pitch?</span>
                  <div className="flex gap-2">
                    {[{ v: true, l: "Yes" }, { v: false, l: "No / not sure" }].map((o) => (
                      <button
                        key={o.l}
                        onClick={() => setNorth(o.v)}
                        data-testid={`calc-north-${o.v ? "yes" : "no"}`}
                        className={`font-display text-sm rounded-full px-6 py-3 border transition-colors duration-300 ${
                          north === o.v
                            ? "bg-[#fcc41b] text-black border-[#fcc41b] font-semibold"
                            : "text-zinc-400 border-[#333] hover:border-[#fcc41b]/50 hover:text-white"
                        }`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div className="rounded-3xl border border-[#262626] bg-[#0d0d0d] p-8 lg:p-12" data-testid="calc-result-panel">
              <div className="flex items-center gap-3 mb-8">
                <Calculator size={22} className="text-[#fcc41b]" />
                <span className="font-display font-semibold text-white text-lg">Your result</span>
              </div>

              {valid ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-[#1c1c1c] p-5">
                      <span className="block font-mono-eng text-[11px] tracking-wider uppercase text-zinc-500 mb-1">Cavity volume</span>
                      <span className="font-display font-bold text-2xl text-white" data-testid="calc-volume">{volume.toLocaleString(undefined, { maximumFractionDigits: 1 })} m³</span>
                    </div>
                    <div className="rounded-2xl border border-[#1c1c1c] p-5">
                      <span className="block font-mono-eng text-[11px] tracking-wider uppercase text-zinc-500 mb-1">Required airflow</span>
                      <span className="font-display font-bold text-2xl text-[#fcc41b]" data-testid="calc-required">{required.toLocaleString()} m³/hr</span>
                    </div>
                  </div>

                  <div className="rounded-2xl bg-[#fcc41b] p-6 text-black" data-testid="calc-recommendation">
                    <span className="block font-mono-eng text-[11px] tracking-wider uppercase opacity-70 mb-2">Recommended</span>
                    {rec.oversized ? (
                      <>
                        <span className="font-display font-bold text-2xl">Large commercial job</span>
                        <span className="mt-2 flex items-center gap-2 text-sm font-medium">
                          <Wind size={16} />
                          Approx. {rec.units} × {rec.model.model} needed — our team will design the best layout
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="font-display font-bold text-3xl">
                          {rec.units > 1 ? `${rec.units} × ` : ""}{rec.model.model}
                        </span>
                        <span className="mt-2 flex items-center gap-2 text-sm font-medium">
                          <Wind size={16} />
                          {rec.units > 1
                            ? `${(flowOf(rec.model) * rec.units).toLocaleString()} m³/hr combined extraction`
                            : `${rec.model.flow} m³/hr extraction`}
                        </span>
                      </>
                    )}
                    {!north && !rec.oversized && <span className="block mt-2 text-xs opacity-80">Tilt-panel model — its solar panel can be angled north on any roof orientation.</span>}
                    {rec.units > 1 && <span className="block mt-2 text-xs opacity-80">
                      {rec.oversized
                        ? "For requirements this large, our team will confirm the exact model mix and layout for your site — request a quote below."
                        : "Larger roofs are best served by multiple units spaced across the roof — our team can confirm the layout for you."}
                    </span>}
                  </div>

                  <button
                    onClick={() => scrollToId("quote")}
                    data-testid="calc-quote-cta"
                    className="group w-full inline-flex items-center justify-center gap-3 rounded-full border border-[#fcc41b]/50 text-[#fcc41b] font-display font-semibold px-8 py-4 hover:bg-[#fcc41b] hover:text-black transition-colors duration-300"
                  >
                    {rec.oversized ? "Talk to our team about this project" : "Get a quote for this model"}
                    <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                  </button>

                  <p className="text-zinc-500 text-xs leading-relaxed">
                    Guide only, based on 10 air changes per hour. Insulation, roof design and use of the space all influence requirements — our team will confirm sizing before you buy.
                  </p>
                </div>
              ) : (
                <p className="text-zinc-500 leading-relaxed" data-testid="calc-empty-state">
                  Enter your roof area and cavity height to see your required airflow and the SolarArk model that delivers it.
                </p>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
